import React, { useState, useEffect } from "react";
import "../../../src/common.css";
import { useLocation, Link } from "react-router-dom";
import { canAccessRouteRule, SIDEBAR_ITEMS } from "../../config/accessControl";
import { useAccess } from "../../context/AccessContext";

// Purely a display/layout grouping — does NOT touch accessControl.js.
// Maps existing route paths (unchanged) into collapsible sidebar sections.
// Any path not listed here still renders exactly as before (flat, ungrouped).
const SIDEBAR_GROUPS = [
  {
    key: "reporting",
    label: "Reporting",
    paths: ["/dashboard", "/reporting", "/dynamic-analytics", "/dynamic-charts"],
  },
  {
    key: "masterdata",
    label: "Master Data",
    paths: [
      "/addprogram",
      "/add-sub-program",
      "/add-fields",
      "/add-subprogram-fields",
      "/add-partner",
      "/partner-form",
    ],
  },
  {
    key: "transitional",
    label: "Transitional",
    paths: ["/add-data", "/view-entry-data", "/group-profile", "/subgroup-profile", "/attendance", "/public-form", "/events"],
  },
  {
    key: "usermanagement",
    label: "User Management",
    paths: ["/adduser", "/addrole"],
  },
];

const GroupIcon = ({ groupKey }) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    focusable: "false",
  };

  switch (groupKey) {
    case "reporting":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="8" height="8" rx="1.6" />
          <rect x="13" y="3" width="8" height="8" rx="1.6" />
          <rect x="3" y="13" width="8" height="8" rx="1.6" />
          <rect x="13" y="13" width="8" height="8" rx="1.6" />
        </svg>
      );
    case "masterdata":
      return (
        <svg {...common}>
          <path d="M4 7.5L12 4l8 3.5-8 3.5L4 7.5z" />
          <path d="M4 12l8 3.5L20 12" />
          <path d="M4 16.5L12 20l8-3.5" />
        </svg>
      );
    case "transitional":
      return (
        <svg {...common}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M8 8h8" />
          <path d="M8 12h8" />
          <path d="M8 16h5" />
        </svg>
      );
    case "usermanagement":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <path d="M4 19c.6-3 2.4-4.5 5-4.5S13.4 16 14 19" />
          <circle cx="16.5" cy="9" r="2.4" />
          <path d="M16 14.6c2.2.2 3.8 1.5 4.4 4.4" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
};

// Hidden from the sidebar entirely (display only — route itself still works
// if linked to directly elsewhere; accessControl.js is untouched).
const HIDDEN_PATHS = ["/add-analytics"];

const PATH_TO_GROUP_KEY = SIDEBAR_GROUPS.reduce((acc, group) => {
  group.paths.forEach((path) => {
    acc[path] = group.key;
  });
  return acc;
}, {});

const LeftSideBar = ({ open, onExpand }) => {
  const [isHovered, setIsHovered] = useState(false);
  // Accordion behavior: only ONE group can be open at a time.
  // `undefined` = user hasn't clicked anything yet -> fall back to
  // whichever group contains the active route (auto-open).
  // Once the user clicks a header, this holds the exact open key
  // (or null if they've explicitly collapsed everything).
  const [openGroupKey, setOpenGroupKey] = useState(undefined);
  const location = useLocation();
  const { permissions, selectedPrivilegeGroups, roleName } = useAccess();

  // Unchanged: same filtering logic/order as before.
  const visibleSidebarItems = SIDEBAR_ITEMS.filter(
    (item) =>
      !HIDDEN_PATHS.includes(item.path) &&
      canAccessRouteRule(item, { permissions, selectedPrivilegeGroups, roleName })
  );

  // Bucket visible items into their display group, preserving original order
  // within each bucket. Items with no group mapping stay ungrouped (flat).
  const groupedItemsByKey = {};
  const ungroupedItems = [];

  visibleSidebarItems.forEach((item) => {
    const groupKey = PATH_TO_GROUP_KEY[item.path];
    if (groupKey) {
      if (!groupedItemsByKey[groupKey]) groupedItemsByKey[groupKey] = [];
      groupedItemsByKey[groupKey].push(item);
    } else {
      ungroupedItems.push(item);
    }
  });

  const isItemActive = (item) =>
    location.pathname === item.path ||
    location.pathname.startsWith(`${item.path}/`);

  // The group that should be open by default (before any manual click):
  // whichever group contains the currently active route, if any.
  const autoOpenGroupKey =
    SIDEBAR_GROUPS.find((group) =>
      (groupedItemsByKey[group.key] || []).some(isItemActive)
    )?.key ?? null;

  // Effective open group: an explicit user choice wins; otherwise fall
  // back to the auto-open (active-route) group.
  const effectiveOpenGroupKey =
    openGroupKey !== undefined ? openGroupKey : autoOpenGroupKey;

  // Accordion toggle: clicking a group's header opens it and collapses
  // every other group. Clicking an already-open group collapses it.
  const toggleGroup = (key) => {
    setOpenGroupKey((prev) => {
      const current = prev !== undefined ? prev : autoOpenGroupKey;
      return current === key ? null : key;
    });
  };

  // True when the sidebar is shown as a narrow icon-only rail: user hasn't
  // toggled it open AND isn't currently hovering it open.
  const isCollapsedRail = !open && !isHovered;

  // Don't remember which group was open once the sidebar collapses back
  // to the icon-only rail — next time it opens, it should start fresh
  // (auto-open only the active route's group, nothing "sticky").
  useEffect(() => {
    if (isCollapsedRail) {
      setOpenGroupKey(undefined);
    }
  }, [isCollapsedRail]);

  const renderItem = (item) => {
    const isActive = isItemActive(item);
    return (
      <li
        key={item.path}
        className={`nav-singl ${isActive ? "active" : ""}`}
      >
        <Link to={item.path} aria-current={isActive ? "page" : undefined}>
          <img src={item.icon} alt="" /> <span>{item.label}</span>
        </Link>
      </li>
    );
  };

  return (
    <div
      className={`left-bar ${!open ? "hideSideBar" : ""} ${
        isHovered ? "result_hover" : ""
      }`}
      data-tour="nav"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to="/dashboard" className="logo">
        <img src="../images/logo_dash.webp" className="img-fluid" alt="iMpower" />
      </Link>
      <ul className="pt-3">
        {SIDEBAR_GROUPS.map((group) => {
          const items = groupedItemsByKey[group.key];
          if (!items || items.length === 0) return null;

          const isOpen = effectiveOpenGroupKey === group.key;
          const groupActive = items.some(isItemActive);

          return (
            <li
              key={group.key}
              className={`nav-singl nav-group ${isOpen ? "sidebar-group-open" : ""} ${
                isCollapsedRail && groupActive ? "active" : ""
              }`}
            >
              {isCollapsedRail ? (
                <button
                  type="button"
                  className="nav-group-header"
                  title={group.label}
                  aria-label={`Open ${group.label} in navigation`}
                  onClick={() => onExpand?.()}
                >
                  <GroupIcon groupKey={group.key} />
                </button>
              ) : (
                <button
                  type="button"
                  className="nav-group-header"
                  title={group.label}
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(group.key)}
                >
                  <span>{group.label}</span>
                  <span
                    className={`nav-caret ${isOpen ? "sidebar-caret-rotated" : ""}`}
                    aria-hidden="true"
                  >
                    &#9656;
                  </span>
                </button>
              )}

              {!isCollapsedRail && isOpen && (
                <ul className="nav-sub-list">{items.map(renderItem)}</ul>
              )}
            </li>
          );
        })}

        {ungroupedItems.map(renderItem)}
      </ul>
    </div>
  );
};

export default LeftSideBar;
